<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactAISummary from 'dashboard/components-next/Contacts/Pages/ContactAISummary.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/Pages/ContactDeleteSection.vue';
import KlaviyoEventsPreview from 'dashboard/components-next/Contacts/Pages/KlaviyoEventsPreview.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import ContactLabelsPanel from 'dashboard/components-next/Contacts/ContactsSidebar/ContactLabelsPanel.vue';
import ContactKlaviyoPanel from 'dashboard/components-next/Contacts/ContactsSidebar/ContactKlaviyoPanel.vue';
import CrmEventCreateDialog from 'dashboard/components-next/Contacts/CrmEvents/CrmEventCreateDialog.vue';
import CloseCirclePreview from 'dashboard/components-next/Contacts/CloseCircle/CloseCirclePreview.vue';
import CloseCircleSheet from 'dashboard/components-next/Contacts/CloseCircle/CloseCircleSheet.vue';
import PurchaseProjectsPreview from 'dashboard/components-next/Contacts/PurchaseProjects/PurchaseProjectsPreview.vue';
import PurchaseProjectsSheet from 'dashboard/components-next/Contacts/PurchaseProjects/PurchaseProjectsSheet.vue';
import NewPurchaseProjectDrawer from 'dashboard/components-next/Contacts/PurchaseProjects/NewPurchaseProjectDrawer.vue';
import { usePurchaseProjectsState } from 'dashboard/components-next/Contacts/PurchaseProjects/usePurchaseProjectsState.js';
import LinkedTasksPreview from 'dashboard/components-next/Tasks/LinkedTasksPreview.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');
const allLabels = useMapGetter('labels/getLabels');
const contactLabels = useMapGetter('contactLabels/getContactLabels');
const contactConversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const contactsDetailsLayoutRef = ref(null);
const contactDetailsRef = ref(null);
const closeCircleSheetRef = ref(null);
const purchaseProjectsSheetRef = ref(null);
const purchaseProjectsPreviewRef = ref(null);
const quickActionDrawerRef = ref(null);
const storeVisitDialogRef = ref(null);
const showContactAiSummary = ref(false);
const { purchaseProjectsForContact, loadPurchaseProjects } =
  usePurchaseProjectsState();

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);

const selectedContactId = computed(() => route.params.contactId);
const selectedContact = computed(() => {
  const contactRecord = contact.value(selectedContactId.value);
  return contactRecord?.id ? contactRecord : null;
});
const selectedContactPreferredChannel = computed(
  () =>
    selectedContact.value?.additionalAttributes?.preferredChannel ||
    selectedContact.value?.additionalAttributes?.preferred_channel ||
    'whatsapp'
);
const contactPurchaseProjects = computed(() =>
  selectedContactId.value
    ? purchaseProjectsForContact(selectedContactId.value)
    : []
);
const activeTab = ref('attributes');

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const { t } = useI18n();

const selectedLabelTitles = computed(() =>
  contactLabels.value(selectedContactId.value)
);

const selectedLabels = computed(() =>
  allLabels.value.filter(({ title }) =>
    selectedLabelTitles.value.includes(title)
  )
);

const labelTabPreview = computed(() => selectedLabels.value.slice(0, 2));

const labelTabPreviewOverflow = computed(() => {
  const overflowCount =
    selectedLabels.value.length - labelTabPreview.value.length;
  return overflowCount > 0 ? `+${overflowCount}` : '';
});

const STORE_VISITS_COUNT = 5;
const ONLINE_HISTORY_COUNT = 3;
const MARKETING_HISTORY_COUNT = 3;

const selectedContactConversations = computed(() =>
  selectedContactId.value
    ? contactConversations.value(selectedContactId.value)
    : []
);

const historyActions = computed(() => [
  {
    label: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.TABS.CONVERSATIONS'),
    value: 'conversations',
    icon: 'i-lucide-messages-square',
    count: selectedContactConversations.value.length,
  },
  {
    label: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.TABS.STORE_VISITS'),
    value: 'storeVisits',
    icon: 'i-lucide-store',
    count: STORE_VISITS_COUNT,
  },
  {
    label: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.TABS.ONLINE_HISTORY'),
    value: 'onlineHistory',
    icon: 'i-lucide-shopping-bag',
    count: ONLINE_HISTORY_COUNT,
  },
  {
    label: t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.TABS.MARKETING_HISTORY'),
    value: 'marketingHistory',
    icon: 'i-lucide-mail-open',
    count: MARKETING_HISTORY_COUNT,
  },
]);

const tabs = computed(() => {
  return [
    {
      label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.ATTRIBUTES'),
      value: 'attributes',
      icon: 'i-lucide-sliders-horizontal',
    },
    {
      label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.NOTES'),
      value: 'notes',
      icon: 'i-lucide-notebook-pen',
    },
    {
      label: t('CONTACTS_LAYOUT.SIDEBAR.TABS.LABELS'),
      value: 'labels',
      icon: 'i-lucide-tags',
    },
    {
      label: t('CONTACT_PANEL.CRM.TAB'),
      value: 'crm',
      icon: 'i-lucide-chart-no-axes-combined',
    },
    {
      label: t('CONTACT_PANEL.MERGE_CONTACT'),
      value: 'merge',
      icon: 'i-lucide-combine',
    },
  ];
});

const mobileSidebarActions = computed(() => tabs.value);

const activeTabIndex = computed(() => {
  return tabs.value.findIndex(v => v.value === activeTab.value);
});

const historyTabValues = [
  'conversations',
  'storeVisits',
  'onlineHistory',
  'marketingHistory',
];

const isHistoryTab = tabValue => historyTabValues.includes(tabValue);

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (selectedContactId.value) {
    await store.dispatch('contacts/show', { id: selectedContactId.value });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      selectedContactId.value
    );
    await loadPurchaseProjects(selectedContactId.value);
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const handleOpenNewPurchaseProject = () => {
  quickActionDrawerRef.value?.open();
};

const handleCloseCircleViewAll = () => {
  closeCircleSheetRef.value?.open();
};

const handleCloseCircleOpenMember = member => {
  closeCircleSheetRef.value?.openMember(member);
};

const handleCloseCircleCreate = () => {
  closeCircleSheetRef.value?.openNewMember();
};

const handleCreateProjectForMember = member => {
  quickActionDrawerRef.value?.open({
    beneficiaryType: 'close_circle_member',
    beneficiaryId: member.id,
    beneficiarySnapshot: {
      firstName: member.firstName,
      lastName: member.lastName,
      relationship: member.relationship,
    },
  });
};

const handlePreviewViewAll = () => {
  purchaseProjectsSheetRef.value?.open();
};

const handlePreviewOpenProject = project => {
  purchaseProjectsSheetRef.value?.openProject(project);
};

const handleKlaviyoViewAll = () => {
  activeTab.value = 'crm';
  contactsDetailsLayoutRef.value?.openMobileSidebar();
};

const openStoreVisitDialog = () => {
  storeVisitDialogRef.value?.open();
};

const handleCloseCircleOpenProject = ({ project, member }) => {
  purchaseProjectsSheetRef.value?.openProject(project, {
    source: 'closeCircle',
    returnContext: { member },
  });
};

const handleReturnToCloseCircle = context => {
  if (context?.member) {
    closeCircleSheetRef.value?.openMember(context.member);
  } else {
    closeCircleSheetRef.value?.open();
  }
};

const onQuickProjectCreated = project => {
  purchaseProjectsPreviewRef.value?.addProject(project);
  purchaseProjectsSheetRef.value?.addProject(project);
};

const onSheetProjectCreated = project => {
  purchaseProjectsPreviewRef.value?.addProject(project);
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const fetchLabels = () => {
  const { contactId } = route.params;
  store.dispatch('labels/get');
  if (contactId) store.dispatch('contactLabels/get', contactId);
};

const openEditContactDialog = () => {
  contactDetailsRef.value?.openEditContactDialog();
};

const toggleContactAiSummary = () => {
  showContactAiSummary.value = !showContactAiSummary.value;
};

const openMobileSidebarTab = tab => {
  activeTab.value = tab.value;
  contactsDetailsLayoutRef.value?.openMobileSidebar();
};

const tabLabelWithCount = tab =>
  tab.count ? `${tab.label} (${tab.count})` : tab.label;

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
  fetchLabels();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-hidden bg-n-background lg:bg-n-surface-1"
  >
    <ContactsDetailsLayout
      ref="contactsDetailsLayoutRef"
      show-mobile-sidebar
      :show-mobile-sidebar-toggle="false"
      mobile-presentation-on-desktop
      @go-to-contacts-list="goToContactsList"
      @open-edit-contact="openEditContactDialog"
      @open-new-purchase-project="handleOpenNewPurchaseProject"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <template v-if="selectedContact && !showSpinner" #header-actions>
        <div class="flex items-center gap-2">
          <Button
            sm
            faded
            slate
            icon="i-lucide-store"
            data-test-id="mark-store-visit-action"
            :label="t('CONTACTS_LAYOUT.STORE_VISIT_ACTION')"
            @click="openStoreVisitDialog"
          />
          <div class="relative">
            <Button
              ghost
              slate
              sm
              icon="i-lucide-sparkles"
              class="!rounded-full bg-n-violet-3 text-n-violet-12 outline outline-1 outline-n-violet-6 hover:!bg-n-violet-4"
              :aria-label="t('CONTACTS_LAYOUT.AI_SUMMARY.BUTTON')"
              @click="toggleContactAiSummary"
            />
            <div
              v-if="showContactAiSummary"
              class="absolute top-full z-30 mt-2 w-[20rem] max-w-[calc(100vw-2rem)] ltr:right-0 rtl:left-0"
            >
              <ContactAISummary
                :selected-contact="selectedContact"
                popover
                class="shadow-xl"
              />
            </div>
          </div>
        </div>
      </template>
      <ContactDetails
        v-if="selectedContact && !showSpinner"
        ref="contactDetailsRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="goToContactsList"
      />
      <section
        v-if="selectedContact && !showSpinner"
        data-test-id="contact-history-actions"
        class="flex flex-col mt-3 bg-n-solid-1"
      >
        <div class="flex items-center justify-between px-4 py-3">
          <div class="min-w-0">
            <h2 class="truncate text-sm font-semibold text-n-slate-12">
              {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.SECTION_TITLE') }}
            </h2>
          </div>
        </div>
        <div class="flex flex-col">
          <template v-for="(tab, index) in historyActions" :key="tab.value">
            <div v-if="index > 0" class="mx-4 h-px bg-n-weak" />
            <button
              type="button"
              class="flex items-center justify-between w-full px-4 py-4 text-sm text-left transition-all duration-200 text-n-slate-12 rounded-none"
              @click="openMobileSidebarTab(tab)"
            >
              <span class="flex items-center min-w-0 gap-3">
                <span
                  class="size-4 shrink-0 text-n-slate-10"
                  :class="tab.icon"
                />
                <span class="flex min-w-0 items-center gap-2">
                  <span class="truncate font-medium">{{ tab.label }}</span>
                  <span class="size-1 rounded-full bg-n-slate-8" />
                  <span
                    data-test-id="history-action-count"
                    class="shrink-0 text-xs font-medium text-n-slate-10"
                  >
                    {{ tab.count }}
                  </span>
                </span>
              </span>
              <span class="i-lucide-chevron-right size-4 text-n-slate-10" />
            </button>
          </template>
        </div>
      </section>
      <CloseCirclePreview
        v-if="selectedContactId && !showSpinner"
        :contact-id="selectedContactId"
        @view-all="handleCloseCircleViewAll"
        @open-member="handleCloseCircleOpenMember"
        @create="handleCloseCircleCreate"
      />
      <PurchaseProjectsPreview
        v-if="selectedContactId && !showSpinner"
        ref="purchaseProjectsPreviewRef"
        :contact-id="selectedContactId"
        @view-all="handlePreviewViewAll"
        @open-project="handlePreviewOpenProject"
        @create="handleOpenNewPurchaseProject"
      />
      <LinkedTasksPreview
        v-if="selectedContact && !showSpinner"
        :contact="selectedContact"
        :title="t('TASKS.CONTACT_BLOCK_TITLE')"
        :flush-on-desktop="false"
      />
      <KlaviyoEventsPreview
        v-if="selectedContact && !showSpinner"
        :selected-contact="selectedContact"
        @view-all="handleKlaviyoViewAll"
      />
      <div
        v-if="selectedContact && !showSpinner"
        data-test-id="contact-sidebar-actions"
        class="flex flex-col gap-px mt-3 bg-n-weak"
      >
        <button
          v-for="tab in mobileSidebarActions"
          :key="tab.value"
          type="button"
          class="flex items-center justify-between w-full px-4 py-4 text-sm text-left transition-all duration-200 text-n-slate-12 bg-n-solid-1 rounded-none"
          @click="openMobileSidebarTab(tab)"
        >
          <span class="flex items-center min-w-0 gap-3">
            <span class="size-4 shrink-0 text-n-slate-10" :class="tab.icon" />
            <span class="flex flex-col min-w-0 gap-1">
              <span class="font-medium">{{ tabLabelWithCount(tab) }}</span>
              <span
                v-if="tab.value === 'labels' && labelTabPreview.length"
                class="flex items-center min-w-0 gap-1 text-xs text-n-slate-11"
              >
                <span
                  v-for="label in labelTabPreview"
                  :key="label.id"
                  class="inline-flex items-center min-w-0 gap-1 max-w-24"
                >
                  <span
                    class="size-1.5 rounded-sm shrink-0"
                    :style="{ backgroundColor: label.color }"
                  />
                  <span class="truncate">{{ label.title }}</span>
                </span>
                <span v-if="labelTabPreviewOverflow" class="shrink-0">
                  {{ labelTabPreviewOverflow }}
                </span>
              </span>
            </span>
          </span>
          <span class="i-lucide-chevron-right size-4 text-n-slate-10" />
        </button>
      </div>
      <div class="hidden">
        <ContactDeleteSection
          v-if="selectedContact && !showSpinner"
          :selected-contact="selectedContact"
          @go-to-contacts-list="goToContactsList"
        />
      </div>
      <template #sidebar>
        <div
          v-if="!isHistoryTab(activeTab)"
          class="overflow-x-auto no-scrollbar px-3"
        >
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            variant="pills"
            @tab-changed="handleTabChange"
          />
        </div>
        <div
          v-if="isFetchingItem"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <template v-else>
          <ContactCustomAttributes
            v-if="activeTab === 'attributes'"
            :selected-contact="selectedContact"
          />
          <ContactLabelsPanel
            v-if="activeTab === 'labels'"
            :selected-contact="selectedContact"
          />
          <ContactNotes v-if="activeTab === 'notes'" />
          <ContactHistory
            v-if="isHistoryTab(activeTab)"
            :history-type="activeTab"
          />
          <ContactKlaviyoPanel
            v-if="activeTab === 'crm' && selectedContact"
            :selected-contact="selectedContact"
          />
          <ContactMerge
            v-if="activeTab === 'merge' && selectedContact"
            :selected-contact="selectedContact"
            @go-to-contacts-list="goToContactsList"
            @reset-tab="handleTabChange(tabs[0])"
          />
        </template>
      </template>
    </ContactsDetailsLayout>

    <CloseCircleSheet
      v-if="selectedContactId"
      ref="closeCircleSheetRef"
      :contact-id="selectedContactId"
      :contact-name="selectedContact?.name ?? ''"
      :contact-phone="selectedContact?.phoneNumber ?? ''"
      :contact-email="selectedContact?.email ?? ''"
      :projects="contactPurchaseProjects"
      @create-project-for-member="handleCreateProjectForMember"
      @open-project="handleCloseCircleOpenProject"
    />

    <PurchaseProjectsSheet
      v-if="selectedContactId"
      ref="purchaseProjectsSheetRef"
      :contact-id="selectedContactId"
      :contact-name="selectedContact?.name ?? ''"
      :contact-phone="selectedContact?.phoneNumber ?? ''"
      :contact-email="selectedContact?.email ?? ''"
      :contact-preferred-channel="selectedContactPreferredChannel"
      @project-created="onSheetProjectCreated"
      @return-to-close-circle="handleReturnToCloseCircle"
    />

    <NewPurchaseProjectDrawer
      v-if="selectedContactId"
      ref="quickActionDrawerRef"
      :contact-id="selectedContactId"
      :contact-name="selectedContact?.name ?? ''"
      :contact-phone="selectedContact?.phoneNumber ?? ''"
      :contact-email="selectedContact?.email ?? ''"
      @created="onQuickProjectCreated"
    />

    <CrmEventCreateDialog
      v-if="selectedContact"
      ref="storeVisitDialogRef"
      :preselected-contact="selectedContact"
      initial-event-type="store_visit"
    />
  </div>
</template>
